import { ethers } from 'ethers';

class NetworkConfigService {
  constructor() {
    this._instance = null;
    this._currentNetwork = null;
    this._provider = null;
    this._contracts = new Map();
    this._listeners = new Set();
    this._networkCache = new Map();
    this._initializationPromise = null;
    
    this.networks = {
      ethereum: {
        id: 'ethereum',
        chainId: 1,
        chainIdHex: '0x1',
        name: 'Ethereum Mainnet',
        rpcUrl: 'https://mainnet.infura.io/v3/your-infura-id',
        blockExplorer: 'https://etherscan.io',
        contracts: {
          batchPayout: process.env.REACT_APP_BATCH_PAYOUT_CONTRACT || '0xFA49b46e3c1677C4973269De91c1Cc6CC0e0a74a',
          owner: process.env.REACT_APP_BATCH_PAYOUT_OWNER || '0x786C4D80FCCA08877ffc0D7538e21194212a6860'
        },
        tokens: {
          USDC: {
            address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
            decimals: 6,
            symbol: 'USDC'
          },
          USDT: {
            address: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
            decimals: 6,
            symbol: 'USDT'
          }
        }
      },
      sepolia: {
        id: 'sepolia',
        chainId: 11155111,
        chainIdHex: '0xaa36a7',
        name: 'Sepolia Testnet',
        rpcUrl: 'https://sepolia.infura.io/v3/your-infura-id',
        blockExplorer: 'https://sepolia.etherscan.io',
        contracts: {
          batchPayout: process.env.REACT_APP_SEPOLIA_BATCH_PAYOUT_CONTRACT || '0x1234567890123456789012345678901234567890',
          owner: process.env.REACT_APP_SEPOLIA_BATCH_PAYOUT_OWNER || '0x1234567890123456789012345678901234567890'
        },
        tokens: {
          USDC: {
            address: '0x1234567890123456789012345678901234567890',
            decimals: 6,
            symbol: 'USDC'
          },
          USDT: {
            address: '0x1234567890123456789012345678901234567890',
            decimals: 6,
            symbol: 'USDT'
          }
        }
      },
      polygon: {
        id: 'polygon',
        chainId: 137,
        chainIdHex: '0x89',
        name: 'Polygon',
        rpcUrl: 'https://polygon-rpc.com',
        blockExplorer: 'https://polygonscan.com',
        contracts: {
          batchPayout: process.env.REACT_APP_POLYGON_BATCH_PAYOUT_CONTRACT,
          owner: process.env.REACT_APP_POLYGON_BATCH_PAYOUT_OWNER
        },
        tokens: {
          USDC: {
            address: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174',
            decimals: 6,
            symbol: 'USDC'
          },
          USDT: {
            address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F',
            decimals: 6,
            symbol: 'USDT'
          }
        }
      }
    };
    
    this.initializeNetwork();
  }
  
  static getInstance() {
    if (!NetworkConfigService._instance) {
      NetworkConfigService._instance = new NetworkConfigService();
    }
    return NetworkConfigService._instance;
  }
  
  initializeNetwork() {
    const networkId = this.resolveNetworkFromEnvironment();
    this._currentNetwork = this.networks[networkId] || this.networks.ethereum;
    
    console.log('[NetworkConfigService] Initialized with network:', {
      id: this._currentNetwork.id,
      chainId: this._currentNetwork.chainId,
      name: this._currentNetwork.name,
      environment: process.env.NODE_ENV,
      forceMainnet: process.env.REACT_APP_FORCE_MAINNET,
      forceNetwork: process.env.REACT_APP_FORCE_NETWORK
    });
  }
  
  resolveNetworkFromEnvironment() {
    if (process.env.REACT_APP_FORCE_NETWORK) {
      return process.env.REACT_APP_FORCE_NETWORK;
    }
    
    if (process.env.REACT_APP_FORCE_MAINNET === 'true') {
      return 'ethereum';
    }
    
    if (process.env.NODE_ENV === 'production') {
      return 'ethereum';
    }
    
    if (process.env.NODE_ENV === 'development') {
      return process.env.REACT_APP_DEFAULT_NETWORK || 'sepolia';
    }
    
    return 'ethereum';
  }
  
  getActiveNetwork() {
    return { ...this._currentNetwork };
  }
  
  getNetworkById(networkId) {
    return this.networks[networkId] || null;
  }
  
  getNetworkByChainId(chainId) {
    const numericChainId = typeof chainId === 'string' ? parseInt(chainId, 16) : chainId;
    return Object.values(this.networks).find(n => n.chainId === numericChainId) || null;
  }
  
  setNetwork(networkId) {
    const network = this.networks[networkId];
    if (!network) {
      throw new Error(`Unknown network: ${networkId}`);
    }
    
    if (this._currentNetwork?.id === networkId) {
      return false;
    }
    
    const previousNetwork = this._currentNetwork;
    this._currentNetwork = network;
    
    this._contracts.clear();
    this._provider = null;
    
    this.notifyListeners({
      type: 'network-changed',
      previousNetwork,
      currentNetwork: network
    });
    
    console.log('[NetworkConfigService] Network changed:', {
      from: previousNetwork?.id,
      to: network.id
    });
    
    return true;
  }
  
  async switchToNetwork(networkId) {
    const network = this.networks[networkId];
    if (!network) {
      throw new Error(`Unknown network: ${networkId}`);
    }
    
    if (!window.ethereum) {
      throw new Error('MetaMask not detected');
    }
    
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: network.chainIdHex }],
      });
      
      this.setNetwork(networkId);
      return true;
    } catch (error) {
      if (error.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: network.chainIdHex,
              chainName: network.name,
              rpcUrls: [network.rpcUrl],
              blockExplorerUrls: [network.blockExplorer],
            }],
          });
          
          this.setNetwork(networkId);
          return true;
        } catch (addError) {
          console.error('Failed to add network:', addError);
          throw addError;
        }
      }
      
      console.error('Failed to switch network:', error);
      throw error;
    }
  }
  
  getContractAddress(contractName) {
    const contracts = this._currentNetwork?.contracts || {};
    return contracts[contractName] || null;
  }
  
  getAllContracts() {
    return { ...(this._currentNetwork?.contracts || {}) };
  }
  
  getTokenAddress(tokenSymbol) {
    const tokens = this._currentNetwork?.tokens || {};
    const token = tokens[tokenSymbol];
    return token?.address || null;
  }
  
  getToken(tokenSymbol) {
    const tokens = this._currentNetwork?.tokens || {};
    return tokens[tokenSymbol] || null;
  }
  
  getAllTokens() {
    return { ...(this._currentNetwork?.tokens || {}) };
  }
  
  getBlockExplorerUrl(type = 'tx', hash = '') {
    const explorer = this._currentNetwork?.blockExplorer || 'https://etherscan.io';
    if (!hash) return explorer;
    
    switch (type) {
      case 'tx':
        return `${explorer}/tx/${hash}`;
      case 'address':
        return `${explorer}/address/${hash}`;
      case 'token':
        return `${explorer}/token/${hash}`;
      default:
        return `${explorer}/${type}/${hash}`;
    }
  }
  
  getProvider() {
    if (!this._provider && window.ethereum) {
      this._provider = new ethers.providers.Web3Provider(window.ethereum);
    }
    return this._provider;
  }
  
  async getContract(contractName, abi) {
    const cacheKey = `${this._currentNetwork.id}-${contractName}`;
    
    if (this._contracts.has(cacheKey)) {
      return this._contracts.get(cacheKey);
    }
    
    const address = this.getContractAddress(contractName);
    if (!address) {
      throw new Error(`Contract ${contractName} not configured for network ${this._currentNetwork.id}`);
    }
    
    const provider = this.getProvider();
    if (!provider) {
      throw new Error('Provider not available');
    }
    
    const signer = await provider.getSigner();
    const contract = new ethers.Contract(address, abi, signer);
    
    this._contracts.set(cacheKey, contract);
    return contract;
  }
  
  subscribe(listener) {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }
  
  notifyListeners(event) {
    this._listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('[NetworkConfigService] Listener error:', error);
      }
    });
  }
  
  isMainnet() {
    return this._currentNetwork?.chainId === 1;
  }
  
  isTestnet() {
    return this._currentNetwork?.id === 'sepolia' || 
           this._currentNetwork?.id === 'goerli' ||
           this._currentNetwork?.id === 'mumbai';
  }
  
  validateNetwork(requiredNetworkId) {
    if (this._currentNetwork?.id !== requiredNetworkId) {
      throw new Error(`Wrong network. Expected ${requiredNetworkId}, got ${this._currentNetwork?.id}`);
    }
    return true;
  }
  
  async detectMetaMaskNetwork() {
    if (!window.ethereum) {
      return null;
    }
    
    try {
      const chainId = await window.ethereum.request({ method: 'eth_chainId' });
      const numericChainId = parseInt(chainId, 16);
      return this.getNetworkByChainId(numericChainId);
    } catch (error) {
      console.error('[NetworkConfigService] Failed to detect MetaMask network:', error);
      return null;
    }
  }
  
  async syncWithMetaMask() {
    const detectedNetwork = await this.detectMetaMaskNetwork();
    if (detectedNetwork && detectedNetwork.id !== this._currentNetwork?.id) {
      this.setNetwork(detectedNetwork.id);
      return true;
    }
    return false;
  }
  
  getDebugInfo() {
    return {
      currentNetwork: this._currentNetwork,
      environment: {
        NODE_ENV: process.env.NODE_ENV,
        REACT_APP_FORCE_MAINNET: process.env.REACT_APP_FORCE_MAINNET,
        REACT_APP_FORCE_NETWORK: process.env.REACT_APP_FORCE_NETWORK,
        REACT_APP_DEFAULT_NETWORK: process.env.REACT_APP_DEFAULT_NETWORK
      },
      contracts: this.getAllContracts(),
      tokens: this.getAllTokens(),
      provider: !!this._provider,
      contractCache: Array.from(this._contracts.keys()),
      listeners: this._listeners.size
    };
  }
  
  reset() {
    this._contracts.clear();
    this._networkCache.clear();
    this._provider = null;
    this.initializeNetwork();
  }
}

export default NetworkConfigService;